import { X } from 'lucide-react';
import { Button, Input, Select } from '@/shared/components';
import type { SearchFieldDto, SearchFilterRule, SearchOperator } from '@/shared/api';
import {
  OPERATOR_LABELS,
  defaultValueFor,
  operatorIsList,
  operatorIsRange,
  operatorNeedsValue,
  valueInputKind,
} from './filterModel';

interface Props {
  rule: SearchFilterRule;
  fields: SearchFieldDto[];
  onChange: (rule: SearchFilterRule) => void;
  onRemove: () => void;
}

export function FilterRuleRow({ rule, fields, onChange, onRemove }: Props) {
  const field = fields.find((f) => f.key === rule.field) ?? fields[0];
  if (!field) return null;

  const changeField = (key: string) => {
    const next = fields.find((f) => f.key === key)!;
    const operator = next.operators.includes(rule.operator) ? rule.operator : next.operators[0]!;
    onChange({ field: key, operator, value: defaultValueFor(operator) });
  };

  const changeOperator = (operator: SearchOperator) =>
    onChange({ ...rule, operator, value: defaultValueFor(operator) });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        aria-label="Feld"
        value={field.key}
        onChange={(e) => changeField(e.target.value)}
        className="min-w-40 flex-1"
      >
        {fields.map((f) => (
          <option key={f.key} value={f.key}>
            {f.key}
          </option>
        ))}
      </Select>

      <Select
        aria-label="Operator"
        value={rule.operator}
        onChange={(e) => changeOperator(e.target.value as SearchOperator)}
        className="min-w-36 flex-1"
      >
        {field.operators.map((op) => (
          <option key={op} value={op}>
            {OPERATOR_LABELS[op]}
          </option>
        ))}
      </Select>

      {operatorNeedsValue(rule.operator) && (
        <ValueInput rule={rule} field={field} onChange={onChange} />
      )}

      <Button
        variant="ghost"
        size="icon"
        aria-label="Regel entfernen"
        onClick={onRemove}
        className="shrink-0"
      >
        <X size={16} strokeWidth={2} />
      </Button>
    </div>
  );
}

function ValueInput({
  rule,
  field,
  onChange,
}: {
  rule: SearchFilterRule;
  field: SearchFieldDto;
  onChange: (rule: SearchFilterRule) => void;
}) {
  const kind = valueInputKind(field.type);
  const set = (value: unknown) => onChange({ ...rule, value });

  if (operatorIsRange(rule.operator)) {
    const range = Array.isArray(rule.value) ? rule.value : ['', ''];
    return (
      <div className="flex flex-[2] items-center gap-2">
        <ScalarField
          kind={kind}
          field={field}
          value={range[0]}
          onChange={(v) => set([v, range[1]])}
        />
        <span className="text-ink-faint text-xs">bis</span>
        <ScalarField
          kind={kind}
          field={field}
          value={range[1]}
          onChange={(v) => set([range[0], v])}
        />
      </div>
    );
  }

  if (operatorIsList(rule.operator)) {
    const list = Array.isArray(rule.value) ? rule.value : [];
    return (
      <Input
        aria-label="Werte (kommagetrennt)"
        className="flex-[2]"
        placeholder="Wert1, Wert2, …"
        value={list.join(', ')}
        onChange={(e) => set(e.target.value.split(',').map((s) => s.trim()))}
      />
    );
  }

  return (
    <div className="flex-[2]">
      <ScalarField kind={kind} field={field} value={rule.value} onChange={set} />
    </div>
  );
}

function ScalarField({
  kind,
  field,
  value,
  onChange,
}: {
  kind: ReturnType<typeof valueInputKind>;
  field: SearchFieldDto;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const str = `${value ?? ''}`;
  if (kind === 'enum' || kind === 'boolean') {
    const options = kind === 'boolean' ? ['true', 'false'] : (field.enumValues ?? []);
    return (
      <Select
        aria-label="Wert"
        value={str}
        onChange={(e) => onChange(e.target.value)}
        className="w-full"
      >
        <option value="" disabled>
          Wählen …
        </option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </Select>
    );
  }
  const type =
    kind === 'number'
      ? 'number'
      : kind === 'date'
        ? 'date'
        : kind === 'datetime'
          ? 'datetime-local'
          : 'text';
  return (
    <Input
      aria-label="Wert"
      className="w-full"
      type={type}
      value={str}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
